import { ICONS, SortableWidgetProps, Tooltip, Widget } from '@creditsights/styleguide-web';
import React, { forwardRef, ReactElement } from 'react';
import { RiskOverviewMetrics } from '../../../models/RiskProducts';
import ScoreCharts from '../../ScoreCharts';
import CircleBar from '../../ScoreCharts/CircleBar';
import ScoreLine from '../../ScoreCharts/ScoreLine';
import { cqsColorConfig, Volatility } from './CqsSummary.constants';
import { formatCqsCircleBarLabel, formatPeriod, formatStepValue, Trend } from './RiskSummary.logic';

export interface CqsSummaryWidgetProps extends SortableWidgetProps {
  riskOverviewMetrics?: RiskOverviewMetrics;
  isError: boolean;
  onErrorRetry: () => void;
}

const CqsSummaryWidget = (
  { riskOverviewMetrics, isError, onErrorRetry, ...sortableWidgetProps }: CqsSummaryWidgetProps,
  ref
): ReactElement | null => {
  const {
    canAccess,
    cqs,
    cqs_trend_1y: trend1year,
    cqs_trend_3y: trend3years,
    cqs_volatility: volatility,
  } = riskOverviewMetrics?.cqs ?? {
    canAccess: true,
    cqs: null,
    cqs_trend_1y: '',
    cqs_trend_3y: '',
    cqs_volatility: '',
  };
  const period = formatPeriod(cqs);

  return (
    <Widget
      headerTitle={
        <Tooltip content="The Credit Quality Score (CQS) can be used to identify companies with upgrade or downgrade potential. The higher the CQS, the higher the medium-term credit quality of the company (ACR is the agency composite rating).">
          <div>Credit Quality Score</div>
        </Tooltip>
      }
      classNames="score-chart-widget"
      fixedHeight
      headerIcon={!canAccess ? { icon: ICONS.LockOutline, dataTestId: 'cqs-lock-icon' } : undefined}
      dataTestId="cqs-summary-widget"
      isError={isError}
      onRetryAfterErrorClick={onErrorRetry}
      ref={ref}
      {...sortableWidgetProps}
    >
      <ScoreCharts
        leftSide={
          <Tooltip content={cqs} disabled={!cqs}>
            <div>
              <CircleBar
                value={cqs || 0}
                color={cqsColorConfig[period]}
                label={formatCqsCircleBarLabel(cqs, canAccess)}
              />
            </div>
          </Tooltip>
        }
        rightSide={
          <>
            <ScoreLine
              title="Trend 1Y"
              steps={Trend}
              activeStep={formatStepValue(trend1year, Trend, canAccess)}
              tooltip="Overall change in a company's Credit Quality Score (CQS) over the last year."
              dataTestId="trend-1-score-line"
            />
            <ScoreLine
              title="Trend 3Y"
              steps={Trend}
              activeStep={formatStepValue(trend3years, Trend, canAccess)}
              tooltip="Overall change in a company's Credit Quality Score (CQS) over the past three years."
              dataTestId="trend-3-score-line"
            />
            <ScoreLine
              title="Volatility"
              steps={Volatility}
              activeStep={formatStepValue(volatility, Volatility, canAccess)}
              tooltip="Volatility of a company's CQS over the past three years relative to all other non-financials companies scored by the mode."
              dataTestId="volatility-score-line"
            />
          </>
        }
      />
    </Widget>
  );
};

export default forwardRef(CqsSummaryWidget);
